import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertController, IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { SharedModule } from '../../components/shared/shared.module';
import { PipesModule } from 'src/app/pipes/pipes.module';
import { UploadComponent } from 'src/app/components/files/upload/upload.component';
import { ExplorerComponent } from '../../components/files/explorer/explorer.component';
import { FilePage } from 'src/app/pop-overs/files/file/file.page';
import { CreateComponent as AppointmentCreateComponent } from '../../components/team-members/calendar/appointments/create/create.component';
import { EmailTypes } from '../EmailTypes';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-email-details',
  templateUrl: './details.page.html',
  styleUrls: ['./details.page.scss']
})
export class DetailsPage implements OnInit {

  @Input() show_header_and_footer = true;
  @Input() hide_headers = false;
  @Input() guid: string = null;
  @Input() email = {
    subject: '',
    description: '',
    guid: '',
    body: '',
    question: '',
    answer: '',
    require_signature: false,
    checklist: null as any,
    type: 0,
    comments: [],
    surveys: [],
    files: [],
    approved: null,
    approval_date: null,
    signature: null,
    requested_documents: [],
    company_guid: null,
    linked_guid: null,
    role_guid: null,
    company: null,
    client: null,
    role: null,
    status: 0,
    auto_remind: false,
    use_firm_days_between: true,
    days_between_email_reminders: 3,
    internal_status: null
  }
  activeIndex = 'overview';
  // Opened from an @mention notification: the comment to scroll to and highlight on the Comments tab
  @Input() focus_comment: any = null;
  finished_loading = false;
  booked_appointment: any = null;
  changed = false;
  showReminderSettings = false;
  emailInternalStatuses: any[] = [];

  GetEmailTypeLabel(): string {
    switch (this.email.type) {
      case EmailTypes.email:               return 'Email';
      case EmailTypes.document_request:    return 'Document Request';
      case EmailTypes.signature_request:   return 'Signature Request';
      case EmailTypes.approval_request:    return 'Approval Request';
      case EmailTypes.survey:              return 'Form Request';
      case EmailTypes.payment_request:     return 'Payment Request';
      case EmailTypes.appointment_request: return 'Appointment Request';
      case EmailTypes.question_request:    return 'Question Request';
      case EmailTypes.checklist_request:   return 'Checklist Request';
      default: return 'Email';
    }
  }

  // Checklist request: the API returns emails.checklist as a JSON string - parsed once per string
  private parsed_checklist_source: any = null;
  private parsed_checklist: any[] = [];

  ChecklistItems(): any[] {
    const source = this.email?.checklist;
    if (source !== this.parsed_checklist_source) {
      this.parsed_checklist_source = source;
      try {
        const items = typeof source === 'string' ? JSON.parse(source) : source;
        this.parsed_checklist = Array.isArray(items) ? items : [];
      } catch {
        this.parsed_checklist = [];
      }
    }
    return this.parsed_checklist;
  }

  ChecklistCheckedCount(): number {
    return this.ChecklistItems().filter((item: any) => item.checked).length;
  }

  // Mirrors table.component.ts's getEmailStatus/getEmailStatusClass (secure-email table) so the
  // status shown here always matches the status shown in the list.
  GetEmailStatus(): string {
    switch ((this.email as any)?.status) {
      case 0: return 'Unopened';
      case 1: return 'Viewed';
      case 2: return 'Files Downloaded';
      case 3: return 'Completed';
      default: return '';
    }
  }

  GetEmailStatusClass(): string {
    switch ((this.email as any)?.status) {
      case 0: return 'badge bg-secondary';
      case 1: return 'badge bg-primary';
      case 2: return 'badge bg-warning text-dark';
      case 3: return 'badge bg-success';
      default: return 'badge bg-secondary';
    }
  }

  // send_method/send_method_email reflect the most recent Create/Resend, set by the backend based on
  // what Global.SendEmail actually used - "outlook"/"gmail" (the connected accountant's own mailbox) or
  // "sendgrid" (the default no_reply@fusionworkflow.com sender).
  GetSendMethodLabel(): string {
    const email: any = this.email;
    if (email?.send_method === 'outlook' || email?.send_method === 'gmail') return 'Sent through your email service (Gmail / Outlook)';
    if (email?.send_method === 'sendgrid') return 'Sent via Fusion Workflow';
    return '';
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.emailInternalStatuses?.length) return null;
    return this.emailInternalStatuses.find(s => s.guid === guid) || null;
  }

  trackStatus(_: number, item: any): string { return item.guid; }

  get emailInternalStatusOptions() {
    return [
      { value: 'CLEAR', label: 'None', description: 'No internal status set.' },
      ...this.emailInternalStatuses.map(s => ({ value: s.guid, label: s.title, description: s.description, color: s.colour }))
    ];
  }

  FormatExpireDate(dateStr: string): string {
    if (!dateStr) return null;
    const d = dayjs(dateStr);
    if (!d.isValid()) return null;
    return d.diff(dayjs(), 'year') >= 25 ? 'Never' : d.format('MMM D, YYYY');
  }

  async GetEmailInternalStatuses() {
    const data: any = await this.apiService.get('tags/emails_internal_status');
    if (data.success) this.emailInternalStatuses = data.data || [];
  }

  async EmailInternalStatusChange() {
    const email: any = this.email;
    await this.apiService.put(`emails/${this.guid}`, { internal_status: email.internal_status });
    this.changed = true;
  }

  constructor(private modalController: ModalController, private alertController: AlertController, public apiService: ApiService, public globalService: GlobalService, private popoverController: PopoverController) { }

  ngOnInit() {
    console.log(this.email);
    this.GetData();

  }

  async presentFilePopover(e, file) {
    e.stopPropagation();
    e.preventDefault();
    const popover = await this.popoverController.create({
      component: FilePage,
      componentProps: { file: file, selected_files: [], minimal: true, show_go_to_folder: true },
      event: e,
      animated: true,
      backdropDismiss: true
    });
    popover.onDidDismiss().then((data) => {
      if (data.data == 4) this.apiService.DownloadFile(file);
      else if (data.data == 8) this.ToggleImportant(file);
      else if (data.data == 9) this.globalService.GoToFileFolder(file.parent_folder_guid);
    });
    return await popover.present();
  }

  async ToggleImportant(file: any) {
    const new_value = !file.is_important;
    const data: any = await this.apiService.put(`files/${file.guid}/important`, { important: new_value });
    if (data.success) {
      file.is_important = new_value;
      this.globalService.presentToastMessage(new_value ? 'Marked as important' : 'Removed from important', true);
    } else {
      this.globalService.presentToastMessage(data.message, false);
    }
  }

  async GetData() {
    this.finished_loading = false;
    this.GetEmailInternalStatuses();
    await this.GetEmailDetails();
    await this.GetEmailFiles();
    if (this.email.type == 2) await this.GetDocumentRequestFiles();
    if (this.email.type == EmailTypes.signature_request) await this.GetSignatureFileGuids();
    if (this.email.type == 5) await this.GetSurveys();
    if (this.email.type == 11 && (this.email as any).status === 3) await this.GetBookedAppointment();
    this.finished_loading = true;
    if (this.activeIndex === 'comments') this.GetComments();
  }

  // The specific file(s) a signature_request email is asking the client to sign - these can never be
  // removed via RemoveFile, even though other files attached alongside the same request can be.
  signature_file_guids: string[] = [];
  async GetSignatureFileGuids() {
    const data: any = await this.apiService.get(`emails/${this.guid}/signature_files`);
    this.signature_file_guids = data?.success ? (data.data || []).map((f: any) => f.file_guid) : [];
  }

  // Governs the remove (x) button next to each "Sent" file: an accountant can only remove a file
  // they themselves attached, and only while the client hasn't opened the email yet (status 0/
  // Unopened) - once it's been viewed the file list locks, since the client may already be relying
  // on what they saw. A signature_request's own document to sign is never removable regardless of status.
  CanRemoveFile(file: any): boolean {
    if ((this.email as any).status != 0) return false;
    if (file.email_files?.[0]?.outside_upload) return false;
    if (this.email.type == EmailTypes.signature_request && this.signature_file_guids.includes(file.guid)) return false;
    return true;
  }

  async RemoveFile(file: any) {
    const alert = await this.alertController.create({
      header: 'Remove File',
      message: `Are you sure you want to remove "${file.file_name}" from this email?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Remove',
          handler: async () => {
            const data: any = await this.apiService.delete(`emails/${this.guid}/files/${file.guid}`, null);
            if (data?.success) {
              this.changed = true;
              await this.GetEmailFiles();
            } else {
              this.globalService.presentToastMessage(data?.message || 'Failed to remove file', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async GetEmailDetails() {
    let data: any = await this.apiService.get(`emails/${this.guid}`);
    console.log("email data", data);
    if (data.success) {
      this.email = data.data[0];
      this.email.requested_documents = [];
      if(this.email.role_guid) {
        let role_data: any = await this.apiService.get(`roles/${this.email.role_guid}`);
        if(role_data.success) this.email.role = role_data.data[0];
      }
      if(this.email.company_guid) {
        let company_data: any = await this.apiService.get(`companies/${this.email.company_guid}`);
        if(company_data.success) this.email.company = company_data.data[0];
      }
      if(this.email.linked_guid){
        let linked_data: any = await this.apiService.get(`clients/${this.email.linked_guid}`);
        if(linked_data.success) this.email.client = linked_data.data[0];
      }
    }
  }

  async Close() {
    this.modalController.dismiss(this.changed ? { changed: true, email: this.email } : null);
  }

  async GoToStep(val) {
    this.activeIndex = val;
    if (val == 'comments') {
      this.GetComments();
    }
  }

  async GetEmailFiles() {
    let data: any = await this.apiService.get(`emails/files/${this.guid}`);
    if(data.success) this.email.files = data.data;
    else this.email.files = [];
  }

  async GetComments() {
    let data: any = await this.apiService.get(`emails/comments/${this.guid}`);
    if (data.success) {
      this.email.comments = data.data;
    } else if (data.status_code == 404) {
      this.email.comments = [];
    }
  }

  async GetDocumentRequestFiles() {
    let data: any = await this.apiService.get(`emails/${this.guid}/document_requests`);
    if(data.success) this.email.requested_documents = data.data;
    else this.email.requested_documents = [];

    console.log("Requested Files:", this.email.requested_documents);
  }
  async GetSurveys() {
    let data: any = await this.apiService.get(`emails/surveys/${this.guid}`);
    if(data.success) this.email.surveys = data.data;
    else this.email.surveys = [];
  }

  async GenerateSurveyPDF(survey) {
    this.apiService.GenerateSurveyPDF(survey.guid);
  }

    @ViewChild('topRowRef', { static: false }) topRowRef: ElementRef | undefined;
      @ViewChild('footerRowRef', { static: false }) footerRowRef: ElementRef | undefined;
      @ViewChild('navRowRef', { static: false }) navRowRef: ElementRef | undefined;
    GetCommentsSize() {
        const el = this.topRowRef.nativeElement as HTMLElement;
        const rect = el.getBoundingClientRect();
        const footerEl = this.footerRowRef.nativeElement as HTMLElement;
        const footerRect = footerEl.getBoundingClientRect();
        const navEl = this.navRowRef.nativeElement as HTMLElement;
        const navRect = navEl.getBoundingClientRect();
        let allowed_size = Math.round(rect.height) - (Math.round(footerRect.height) + Math.round(navRect.height));
        console.log("Allowed Size:", allowed_size);
        return allowed_size - 1;
    }


    GetMatchingFile(guid) {
    for (let i = 0; i < this.email.files.length; i++) {
      if (this.email.files[i].guid == guid) {
        return this.email.files[i];
      }
    }
  }
  GetMatchingRequest(guid) {
    if (this.email.type != 2) return null;
    if (!this.email.requested_documents) return true;
    for (let i = 0; i < this.email.requested_documents.length; i++) {
      if (this.email.requested_documents[i].guid == guid) {
        return this.email.requested_documents[i].file_guid;
      }
    }
  }

  // Returns every file uploaded against a specific named document request, so multiple
  // uploads to the same request all show up (not just the most recent one).
  GetFilesForDocumentRequest(doc: any): any[] {
    if (doc.uploaded_files && doc.uploaded_files.length > 0) {
      return doc.uploaded_files.map((f: any) => ({ guid: f.file_guid, file_name: f.file_name, created_date: f.created_date, is_important: f.is_important }));
    }
    // Legacy fallback: requests fulfilled before multi-file uploads only have a single file_guid
    if (doc.file_guid) {
      const f = this.GetMatchingFile(doc.file_guid);
      return f ? [f] : [{ guid: doc.file_guid, file_name: doc.name }];
    }
    return [];
  }

  async GetBookedAppointment() {
    const data: any = await this.apiService.get(`emails/${this.guid}/appointment_request/booking`);
    if (data?.success) {
      const appt = Array.isArray(data.data) ? data.data[0] : data.data;
      if (appt) {
        if (appt.start_time && !appt.start_time.endsWith('Z')) appt.start_time += 'Z';
        if (appt.end_time && !appt.end_time.endsWith('Z')) appt.end_time += 'Z';
      }
      this.booked_appointment = appt;
    } else {
      (this.email as any).status = 1;
    }
  }

  async OpenAppointmentModal(is_rescheduling = false) {
    const email: any = this.email;
    let assigned_companies = [];
    let assigned_clients = [];
    let assigned_role = null;
    if(this.email.role_guid) {
      assigned_role = this.email.role_guid;
    }
    if(this.email.company) {
      assigned_companies.push(this.email.company);
    }
    else if (this.email.client) {
      assigned_clients.push(this.email.client);
    }
    const team_member_guids = [];
    if(this.booked_appointment.team_members) {
      for(let tm of this.booked_appointment.team_members) {
        team_member_guids.push(tm.guid);
      }
    }
    console.log("Current Assigned Team Members:", team_member_guids);

    console.log("Opening appointment modal for email:", email);
    const modal = await this.modalController.create({
      component: AppointmentCreateComponent,
      cssClass: 'tall-modal secondary-modal',
      backdropDismiss: false,
      componentProps: {
        start_time: new Date(),
        team_member: this.globalService.GetCurrentUser(),
        in_selected_users: team_member_guids,
        force_type: 1,
        email_guid: this.guid,
        is_rescheduling: is_rescheduling,
        in_assigned_clients: assigned_clients || [],
        in_assigned_companies: assigned_companies || [],
        in_assigned_role: assigned_role,
        allow_assigned_change: false,
        title: this.booked_appointment !== null ? this.booked_appointment.title : `Appointment for ${email.subject}`,
        description: this.booked_appointment !== null ? this.booked_appointment.description : ``,
      }
    });
    modal.onDidDismiss().then(async (result: any) => {
      if (result?.data?.refresh) {
        (this.email as any).status = 3;
        await this.GetBookedAppointment();
      }
    });
    return await modal.present();
  }

  async CancelAppointment() {
    const alert = await this.alertController.create({
      header: 'Cancel Appointment',
      message: 'Are you sure you want to cancel this appointment?',
      buttons: [
        { text: 'No', role: 'cancel' },
        {
          text: 'Yes, Cancel It',
          cssClass: 'alert-button-danger',
          handler: async () => {
            const data: any = await this.apiService.delete(
              `emails/${this.guid}/appointment_request/book`,
              { tz_str: Intl.DateTimeFormat().resolvedOptions().timeZone }
            );
            if (data?.success) {
              this.globalService.presentToastMessage('Appointment cancelled', true);
              (this.email as any).status = 1;
              this.booked_appointment = null;
            } else {
              this.globalService.presentToastMessage(data?.message || 'Failed to cancel appointment', false);
            }
          }
        }
      ]
    });
    await alert.present();
  }

  // Figures out where the file explorer picker should start when adding an existing file to this
  // email. Only ever resolves to a project folder (never a task's own subfolder, even though a
  // task always belongs to a project and files under a task folder do carry a project_guid too -
  // see ResolveUploadProjectTaskLinks on the API side). Primary source is the email's own step's
  // project, returned directly by emails/{guid} via a join to steps - covers step-generated emails
  // even before any file has been attached (a pending document/signature request, say). Falls back
  // to inferring from wherever an already-attached file lives, then to the linked company/client's
  // root folder, then Home, if no project linkage can be found at all.
  async GetLinkedRootFolderGuid(): Promise<string> {
    const email: any = this.email;

    if (email.step_project_guid) {
      const project_data: any = await this.apiService.get(`projects/${email.step_project_guid}`);
      const project = project_data?.success && project_data.data && project_data.data[0];
      if (project?.folder_guid) return project.folder_guid;
    }

    const files = email.files || [];
    if (files.length > 0) {
      const infos: any[] = await Promise.all(files.map((f: any) => this.apiService.get(`files/${f.guid}/info`)));
      const file_infos = infos
        .map((d: any) => (d?.success && d.data && d.data[0]) ? d.data[0] : null)
        .filter(Boolean);

      const with_project = file_infos.find((f: any) => f.project_guid);
      if (with_project) {
        const project_data: any = await this.apiService.get(`projects/${with_project.project_guid}`);
        const project = project_data?.success && project_data.data && project_data.data[0];
        if (project?.folder_guid) return project.folder_guid;
      }
    }

    const company: any = email.company;
    if (company?.folder?.[0]?.guid) return company.folder[0].guid;
    const client: any = email.client;
    if (client?.folder?.[0]?.guid) return client.folder[0].guid;

    return '0';
  }

  async SelectExistingFile() {
    const root_folder_guid = await this.GetLinkedRootFolderGuid();
    const company: any = this.email.company;
    const client: any = this.email.client;
    const modal = await this.modalController.create({
      component: ExplorerComponent,
      componentProps: {
        file_select_mode: true,
        root_folder_guid,
        linked_client_guid: client?.guid || null,
        linked_company_guid: company?.guid || null,
        linked_entity_name: company?.name || client?.display_name || null,
        // Picking a file here attaches it to an email the client will receive - an Internal Only
        // file shouldn't be pickable to send to them in the first place.
        show_hidden_to_client: false
      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.length > 0) {
      await this.globalService.presentLoading('Attaching files...', 30000);
      const file_list = data.map((f: any) => ({ guid: f.guid, file_name: f.file_name, was_selected: true }));
      await this.apiService.put(`emails/${this.guid}`, { file_list });
      this.globalService.dismissLoading();
      await this.GetData();
    }
  }

  async UploadFileToEmail() {
    const modal = await this.modalController.create({
      component: UploadComponent,
      cssClass: 'small-modal secondary-modal',
      componentProps: {
        folder_guid: '0',
        additional_data: { email_guid: this.guid }
      },
    });
    modal.present();
    const { data } = await modal.onDidDismiss();
    if (data && data.length > 0) {
      // if ((this.email as any).status > 0) {
      //   await this.apiService.post(`emails/${this.guid}/notify_file_upload`, {});
      // }
      await this.GetData();
    }
  }

  async UploadToEmailDocumentRequest(doc: any) {
    let additional_data = {
      document_request_guid: doc.guid,
      email_guid: this.guid
    }
    const modal = await this.modalController.create({
      component: UploadComponent,
      cssClass: 'small-modal secondary-modal',
      componentProps: {
        folder_guid: '0',
        additional_data: additional_data,
        title: 'Add Files to Email'
      },
    });
    modal.present();

    const { data } = await modal.onDidDismiss();
    if (data && data.length > 0) {
      await this.GetData();
    }
  }

  async ResendEmail() {
    const alert = await this.alertController.create({
      header: 'Resend Email',
      message: 'Are you sure you want to resend this email to the recipient?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Resend',
          handler: async () => {
            const data: any = await this.apiService.put(`emails/resend/${this.guid}`, null);
            this.globalService.presentToastMessage(data?.message || (data?.success ? 'Email resent' : 'Failed to resend email'), data?.success);
          }
        }
      ]
    });
    await alert.present();
  }

  async SaveReminderSettings() {
    const email: any = this.email;
    const data: any = await this.apiService.put(`emails/${this.guid}`, {
      auto_remind: email.auto_remind ?? false,
      use_firm_days_between: email.use_firm_days_between ?? true,
      days_between_email_reminders: email.days_between_email_reminders ?? 3
    });
    if (data.success) this.changed = true;
    this.globalService.presentToastMessage(data.message, data.success);
  }

  async Log() {
    console.log(this.email);
  }

}
